import { useEffect, useState, type ReactNode } from 'react';
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { cn } from '@/lib/utils';

interface CollapsibleSectionProps {
  // Stable identifier — drives the localStorage key used to
  // persist this section's open/closed state across reloads.
  id: string;
  title: string;
  // Tiny line under the title — useful for summary stats ("12
  // running, 0 failed") so the operator can decide whether to
  // expand without doing so.
  summary?: ReactNode;
  // Optional right-aligned slot for actions tied to the section
  // header (e.g. a "Refresh" link). Shown only when expanded so
  // it doesn't dominate the collapsed-header row.
  rightSection?: ReactNode;
  defaultOpen?: boolean;
  children: ReactNode;
}

const STORAGE_PREFIX = 'theia.section.';

function readOpenState(id: string, fallback: boolean): boolean {
  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}${id}.open`);
    if (raw === null) return fallback;
    return raw === '1';
  } catch {
    return fallback;
  }
}

function writeOpenState(id: string, open: boolean): void {
  try {
    window.localStorage.setItem(
      `${STORAGE_PREFIX}${id}.open`,
      open ? '1' : '0',
    );
  } catch {
    /* private mode / quota / disabled — silently no-op */
  }
}

// Collapsible content panel with a click-anywhere header. Matches
// the dash.cloudflare.com sidebar grouping pattern adapted for
// in-page sections: small chevron + uppercase title + optional
// summary line, the whole row is the click target. Animates open
// via a CSS max-height transition.
//
// Persistence: keyed by `id`. The user's last choice survives
// page reloads via localStorage.
export function CollapsibleSection({
  id,
  title,
  summary,
  rightSection,
  defaultOpen = true,
  children,
}: CollapsibleSectionProps) {
  const [open, setOpen] = useState<boolean>(() => readOpenState(id, defaultOpen));

  useEffect(() => {
    writeOpenState(id, open);
  }, [id, open]);

  return (
    <section aria-labelledby={`section-${id}-title`}>
      <div className="flex w-full items-center gap-3 rounded-sm px-1 has-[button:hover]:bg-neutral-800">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={`section-${id}-content`}
          className={cn(
            'group flex min-w-0 flex-1 items-center gap-2 bg-transparent py-2 text-left',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          )}
        >
          {open ? (
            <IconChevronDown size={14} stroke={2} className="text-muted-foreground shrink-0" />
          ) : (
            <IconChevronRight size={14} stroke={2} className="text-muted-foreground shrink-0" />
          )}
          <span
            id={`section-${id}-title`}
            className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground"
          >
            {title}
          </span>
          {summary && (
            <span className="min-w-0 truncate text-xs text-muted-foreground">
              {summary}
            </span>
          )}
        </button>
        {/* `rightSection` lives as a sibling of the toggle button.
            Putting interactive elements inside a <button> is invalid
            HTML (React 19 warns) and click handling on the inner
            controls is fragile. Sibling layout keeps both surfaces
            independent. */}
        {open && rightSection && <div>{rightSection}</div>}
      </div>
      <div
        id={`section-${id}-content`}
        style={{
          overflow: 'hidden',
          // CSS-only max-height transition. 5000 px is a generous
          // cap; admin sections top out well under that.
          transition:
            'max-height 220ms ease, opacity 180ms ease, visibility 0s linear ' +
            (open ? '0s' : '220ms'),
          maxHeight: open ? '5000px' : '0px',
          opacity: open ? 1 : 0,
          visibility: open ? 'visible' : 'hidden',
        }}
      >
        <div className="pb-4 pt-2">{children}</div>
      </div>
    </section>
  );
}
